'use client'

import { useEffect, useState } from 'react'
import { capabilities, profile, projects, tools, type Project } from './content'
import AfterEightScrapbook from './AfterEightScrapbook'
import { ProjectSideArt, ProjectThemeDoodle } from './ProjectDoodles'
import { UnfinishedScrapbook, WeekendScrapbook } from './ProjectScrapbooks'

const navItems = [
  ['top', '你好'],
  ['about', '关于我'],
  ['work', '项目'],
  ['contact', '联系'],
]

function Scribble({ className = '' }: { className?: string }) {
  return (
    <svg className={`scribble ${className}`} viewBox="0 0 180 64" aria-hidden="true">
      <path d="M5 42C34 11 50 57 76 31S123 7 174 28" />
      <path d="M139 12c14 3 24 11 35 16-12 4-18 10-27 24" />
    </svg>
  )
}

function Spark({ className = '' }: { className?: string }) {
  return (
    <svg className={`spark ${className}`} viewBox="0 0 100 100" aria-hidden="true">
      <path d="M50 5c2 29 10 39 43 44-31 4-41 14-44 46C45 64 35 53 6 50 36 45 46 35 50 5Z" />
    </svg>
  )
}

function InteractiveProjectBoard({ projectId }: { projectId: string }) {
  if (projectId === 'after-eight') return <AfterEightScrapbook />
  if (projectId === 'unfinished') return <UnfinishedScrapbook />
  return <WeekendScrapbook />
}

function CaseLogicSnapshot({ project }: { project: Project }) {
  if (!project.caseLogic) return null

  return (
    <section className="case-logic" aria-labelledby={`${project.id}-logic-title`}>
      <header>
        <div>
          <span className="eyebrow">CASE STRATEGY AT A GLANCE</span>
          <h4 id={`${project.id}-logic-title`}>我的策划解题路径</h4>
        </div>
        <p>先看问题、目标和推导，再看创意长什么样。</p>
      </header>
      <div className="logic-briefs">
        <article>
          <span>01｜商业课题 BUSINESS CHALLENGE</span>
          <p>{project.caseLogic.businessQuestion}</p>
        </article>
        <article>
          <span>02｜传播目标 COMMUNICATION OBJECTIVE</span>
          <p>{project.caseLogic.objective}</p>
        </article>
      </div>
      <ol className="logic-route" aria-label="策略推导路径">
        {project.caseLogic.route.map((item, index) => (
          <li key={item.label}>
            <span>{String(index + 1).padStart(2, '0')}</span>
            <strong>{item.label}</strong>
            <p>{item.detail}</p>
          </li>
        ))}
      </ol>
      <div className="logic-outputs">
        <strong>核心交付物</strong>
        <div>
          {project.caseLogic.outputs.map((item) => (
            <span key={item.name} title={item.detail}><b>{item.name}</b>{item.detail}</span>
          ))}
        </div>
      </div>
    </section>
  )
}

function ProjectSection({ project, position }: { project: Project; position: number }) {
  const [detailsOpen, setDetailsOpen] = useState(false)

  return (
    <article id={project.id} className={`project project--${project.color} reveal`}>
      <div className="project-heading">
        <div>
          <span className="eyebrow">CASE {project.index} · {project.kind}</span>
          <h3>{project.title}</h3>
          <p className="project-subtitle">{project.subtitle}</p>
          {project.brandNote && <p className="project-brand-note">{project.brandNote}</p>}
        </div>
        <ProjectThemeDoodle projectId={project.id} />
        <span className="project-number" aria-hidden="true">{project.index}</span>
      </div>

      <CaseLogicSnapshot project={project} />

      <div className={`project-cover ${position % 2 ? 'project-cover--reverse' : ''} ${detailsOpen ? 'is-details-open' : ''}`}>
        {project.interactive ? (
          <div className="project-side">
            <InteractiveProjectBoard projectId={project.id} />
            <ProjectSideArt projectId={project.id} />
          </div>
        ) : (
          <figure className="project-visual">
            <img
              src={project.image}
              alt={project.imageAlt}
              loading={position === 0 ? 'eager' : 'lazy'}
              fetchPriority={position === 0 ? 'high' : 'auto'}
            />
            <span className="tape tape--top" aria-hidden="true" />
            <span className="tape tape--bottom" aria-hidden="true" />
          </figure>
        )}
        <div className="project-intro">
          <p className="disclaimer">{project.disclaimer}</p>
          <p>{project.brief}</p>
          <div className="concept-note">
            <span>核心创意</span>
            <strong>{project.concept}</strong>
          </div>
          <details className="case-details" onToggle={(event) => setDetailsOpen(event.currentTarget.open)}>
            <summary>
              <span className="summary-closed">{project.caseLogic ? '展开执行方案与项目底稿' : '展开完整策划'}</span>
              <span className="summary-open">收起策划内容</span>
              <span className="summary-icon" aria-hidden="true">＋</span>
            </summary>
            <div className="details-content">
              <div className="insight-grid">
                <section>
                  <span className="mini-label">TA 是谁</span>
                  <p>{project.audience}</p>
                </section>
                <section>
                  <span className="mini-label">关键洞察</span>
                  <p>{project.insight}</p>
                </section>
                <section>
                  <span className="mini-label">策略转译</span>
                  <p>{project.strategy}</p>
                </section>
              </div>

              <section className="execution-section">
                <div className="subheading-row">
                  <h4>传播与落地</h4>
                  <span>HOW IT COMES ALIVE</span>
                </div>
                <ProjectThemeDoodle projectId={project.id} />
                <div className="execution-grid">
                  {project.executions.map((execution, index) => (
                    <div className="execution-card" key={execution.title}>
                      <span>{String(index + 1).padStart(2, '0')}</span>
                      <h5>{execution.title}</h5>
                      <p>{execution.detail}</p>
                    </div>
                  ))}
                </div>
              </section>

              {project.caseLogic && (
                <section className="workplan-section">
                  <div className="subheading-row">
                    <div>
                      <span className="mini-label">FROM IDEA TO DELIVERY</span>
                      <h4>项目推进与交付计划</h4>
                    </div>
                    <p>概念项目不伪造实绩；这里展示我对真实协作、文件交付和上线复盘的拆解。</p>
                  </div>
                  <div className="workplan-list">
                    {project.caseLogic.workplan.map((item) => (
                      <article key={item.phase}>
                        <strong>{item.phase}</strong>
                        <p>{item.action}</p>
                        <span>交付：{item.handoff}</span>
                      </article>
                    ))}
                  </div>
                </section>
              )}

              {project.validation && project.tradeoffs && project.risk ? (
                <div className="decision-section">
                  <section className="validation-map">
                    <div className="subheading-row">
                      <div>
                        <span className="mini-label">STRATEGY → BEHAVIOR → SIGNAL</span>
                        <h4>传播动作如何被验证</h4>
                      </div>
                      <p>不是在方案末尾贴一组数字，而是检查每个动作是否真的推动了预期行为。</p>
                    </div>
                    <div className="validation-list">
                      {project.validation.map((item, index) => (
                        <article key={item.action}>
                          <span>{String(index + 1).padStart(2, '0')}</span>
                          <div><small>策划动作</small><strong>{item.action}</strong></div>
                          <i aria-hidden="true">→</i>
                          <div><small>希望发生</small><p>{item.behavior}</p></div>
                          <i aria-hidden="true">→</i>
                          <div><small>观察证据</small><p>{item.signal}</p></div>
                        </article>
                      ))}
                    </div>
                  </section>

                  <div className="decision-grid">
                    <section className="tradeoff-card">
                      <span className="mini-label">MY DECISIONS</span>
                      <h4>我做的关键取舍</h4>
                      <ol>
                        {project.tradeoffs.map((item) => <li key={item}>{item}</li>)}
                      </ol>
                    </section>
                    <section className="risk-card">
                      <span className="mini-label">REALITY CHECK</span>
                      <h4>真落地时最可能卡在哪里？</h4>
                      <strong>{project.risk.title}</strong>
                      <p>{project.risk.response}</p>
                    </section>
                  </div>
                </div>
              ) : (
                <div className="outcome-grid">
                  <section>
                    <h4>建议 KPI</h4>
                    <p className="metric-note">衡量目标，不伪装为项目实绩</p>
                    <ul>{project.kpis.map((kpi) => <li key={kpi}>{kpi}</li>)}</ul>
                  </section>
                  <section>
                    <h4>我的策划输出</h4>
                    <ul>{project.contribution.map((item) => <li key={item}>{item}</li>)}</ul>
                  </section>
                </div>
              )}
            </div>
          </details>
        </div>
      </div>
    </article>
  )
}

function App() {
  const [activeSection, setActiveSection] = useState('top')

  useEffect(() => {
    const hashTarget = window.location.hash.slice(1) || new URLSearchParams(window.location.search).get('section') || ''
    const deepLinkTimer = window.setTimeout(() => {
      if (hashTarget) document.getElementById(hashTarget)?.scrollIntoView({ block: 'start' })
    }, 120)

    const revealObserver = new IntersectionObserver(
      (entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) entry.target.classList.add('is-visible')
      }),
      { threshold: 0.12 },
    )

    document.querySelectorAll('.reveal').forEach((element) => revealObserver.observe(element))

    const sectionObserver = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0]
        if (visible) setActiveSection(visible.target.id)
      },
      { rootMargin: '-35% 0px -55% 0px', threshold: [0, 0.2, 0.6] },
    )

    navItems.forEach(([id]) => {
      const element = document.getElementById(id)
      if (element) sectionObserver.observe(element)
    })

    return () => {
      window.clearTimeout(deepLinkTimer)
      revealObserver.disconnect()
      sectionObserver.disconnect()
    }
  }, [])

  return (
    <>
      <header className="site-header" aria-label="主导航">
        <a className="mini-mark" href="#top" aria-label="返回首页">策</a>
        <nav>
          {navItems.map(([id, label]) => (
            <a key={id} href={`#${id}`} className={activeSection === id ? 'is-active' : ''}>
              {label}
            </a>
          ))}
        </nav>
        <span className="availability"><i /> OPEN TO WORK</span>
      </header>

      <main id="main">
        <section id="top" className="hero">
          <div className="hero-doodle hero-doodle--sun" aria-hidden="true">
            <span />
          </div>
          <div className="hero-copy reveal is-visible">
            <p className="eyebrow">HELLO, 我是<span className="hand-underline">{profile.name}</span></p>
            <h1>{profile.tagline.split('\n').map((line) => <span key={line}>{line}</span>)}</h1>
            <div className="hero-meta">
              <p>{profile.role}</p>
              <a className="text-link" href="#work">去看项目 <span aria-hidden="true">↘</span></a>
            </div>
          </div>

          <div className="portrait-wrap reveal is-visible">
            <div className="portrait-placeholder" role="img" aria-label="个人照片待补充">
              <div className="portrait-face" aria-hidden="true">
                <span className="eye eye--left" />
                <span className="eye eye--right" />
                <span className="smile" />
              </div>
              <strong>PHOTO<br />HERE</strong>
              <small>真实照片待补充</small>
            </div>
            <span className="sticker sticker--name">THIS IS ME!</span>
            <Scribble className="portrait-arrow" />
          </div>

          <div className="hero-footnote">
            <span>{profile.location}</span>
            <span>{profile.education}</span>
            <span>© 2026 PORTFOLIO</span>
          </div>
        </section>

        <section id="about" className="about section-shell">
          <div className="section-title reveal">
            <span className="eyebrow">ABOUT ME · 关于我</span>
            <h2>我在意的，<br />不只是一个“好点子”</h2>
            <Spark className="title-spark" />
          </div>

          <div className="about-story reveal">
            <p>{profile.bio}</p>
            <div className="method-line" aria-label="我的策划方法">
              <span>观察</span><i>→</i><span>提问</span><i>→</i><span>策略</span><i>→</i><span>体验</span><i>→</i><span>复盘</span>
            </div>
          </div>

          <div className="capability-grid">
            {capabilities.map(([number, title, text], index) => (
              <article className={`capability-card capability-card--${index + 1} reveal`} key={number}>
                <span>{number}</span>
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>

          <div className="toolbox reveal">
            <span className="toolbox-title">我的工具袋里有</span>
            <div>
              {tools.map((tool) => <span key={tool}>{tool}</span>)}
            </div>
          </div>
        </section>

        <section id="work" className="work section-shell">
          <div className="work-intro reveal">
            <div>
              <span className="eyebrow">SELECTED WORK · 自主命题</span>
              <h2>三个问题，<br />三种回答方式</h2>
            </div>
            <p>这些项目没有假装发生过。它们是我为真实营销问题做的完整练习：从为什么开始，到如何落地，再到怎样判断它有没有用。</p>
          </div>

          <div className="project-list">
            {projects.map((project, index) => (
              <ProjectSection key={project.id} project={project} position={index} />
            ))}
          </div>
        </section>

        <section id="contact" className="contact section-shell">
          <div className="contact-card reveal">
            <span className="eyebrow">LET'S TALK · 保持联系</span>
            <h2>下一次好玩的事，<br />可以一起想。</h2>
            <p>姓名、邮箱与正式简历补充后，这里会开放真实联系入口。当前预览版不会收集或发送任何个人信息。</p>
            <div className="contact-actions">
              <button type="button" disabled aria-describedby="email-status">邮箱待补充</button>
              <button type="button" className="button-secondary" disabled aria-describedby="resume-status">简历待上传</button>
            </div>
            <div className="sr-only" aria-live="polite">
              <span id="email-status">邮箱尚未提供，按钮暂不可用。</span>
              <span id="resume-status">简历尚未上传，按钮暂不可用。</span>
            </div>
            <Scribble className="contact-arrow" />
            <span className="contact-sticker">WAITING<br />FOR YOU</span>
          </div>

          <footer>
            <span>{profile.name} · 营销策划作品集</span>
            <span>内容为自主命题，仅用于个人求职展示</span>
            <a href="#top">回到顶部 ↑</a>
          </footer>
        </section>
      </main>
    </>
  )
}

export default App
